import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { isVersionSupported, storeCurrentVersion } from '../lib/version';
import { clearAccessCheckCache, checkUserAccess, getUserAccessInfo, type AccessCheckResult } from '../services/billing';
import type { UserAccessInfo } from '../types/billing';
import type { Profile } from '../types/profile';
import { devTime, devTimeEnd, devLog, devWarn, logCriticalError } from '../lib/devLog';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  needsUpdate: boolean;
  accessResult: AccessCheckResult | null;
  accessInfo: UserAccessInfo | null;
  profile: Profile | null;
  checkingAccess: boolean;
  refreshAccessCheck: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: (navigate: (path: string, opts?: { replace: boolean }) => void) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [accessResult, setAccessResult] = useState<AccessCheckResult | null>(null);
  const [accessInfo, setAccessInfo] = useState<UserAccessInfo | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const userIdRef = useRef<string | undefined>(undefined);

  // Poll for version changes every 15 minutes
  useEffect(() => {
    if (!user) return;

    const checkVersion = () => {
      setNeedsUpdate(!isVersionSupported());
    };
    checkVersion();
    
    // Check every 12 hours (43200000 ms)
    const interval = setInterval(checkVersion, 43200000);

    return () => clearInterval(interval);
  }, [user]);

  // Check if update is needed
  useEffect(() => {
    if (user) {
      setNeedsUpdate(!isVersionSupported());
    }
  }, [user]);

  // Update userIdRef whenever user changes
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  // Check access and load profile when user logs in or changes
  useEffect(() => {
    const performAccessCheck = async () => {
      if (!user) {
        setAccessResult(null);
        setAccessInfo(null);
        setProfile(null);
        setCheckingAccess(false);
        return;
      }

      devTime('[Auth] Access check');
      setCheckingAccess(true);
      try {
        const result = await checkUserAccess(user.id);
        setAccessResult(result);
        devTimeEnd('[Auth] Access check');
        devLog('[Auth] ✓ Access verified');

        // PERFORMANCE FIX: Load profile and access info in parallel immediately after access check
        // No setTimeout needed - they can start as soon as access is verified
        if (result.hasAccess) {
          // Load both in parallel using Promise.all for faster load time
          Promise.all([
            getUserAccessInfo(user.id).catch(err => {
              devWarn('[AuthContext] Could not fetch access info:', err);
              return null;
            }),
            supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .maybeSingle()
              .then(({ data }) => {
                // Cache grandfathered status from profile for faster future checks
                if (data?.grandfathered) {
                  devLog('[AuthContext] ✓ Grandfathered user detected from profile');
                }
                return data;
              })
              .catch(err => {
                devWarn('[AuthContext] Could not fetch profile:', err);
                return null;
              })
          ]).then(([info, profileData]) => {
            setAccessInfo(info);
            setProfile(profileData);
          });
        }
      } catch (error) {
        logCriticalError('[AuthContext] Access check failed', error);
        devTimeEnd('[Auth] Access check');
        setAccessResult({ hasAccess: false, error: true, errorMessage: 'Access check failed' });
        setAccessInfo(null);
        setProfile(null);
      } finally {
        setCheckingAccess(false);
      }
    };

    performAccessCheck();
  }, [user?.id]); // Only depend on user ID, not the entire user object

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        storeCurrentVersion();
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const previousUserId = userIdRef.current;
      const newUserId = session?.user?.id;

      setUser(session?.user ?? null);

      // CRITICAL FIX: Only clear cache if userId actually changed (not on session refresh)
      if (previousUserId !== newUserId) {
        devLog('[AuthContext] User changed, clearing access cache');
        clearAccessCheckCache();
        setAccessResult(null);
      } else if (event === 'TOKEN_REFRESHED' && newUserId) {
        devLog('[AuthContext] Session refreshed, keeping existing access cache');
        // Don't clear cache or re-check access - session refresh is automatic
      }

      // Store version on successful sign in
      if (session?.user) {
        storeCurrentVersion();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshAccessCheck = async () => {
    if (!user) return;

    setCheckingAccess(true);
    try {
      const result = await checkUserAccess(user.id, true); // Force refresh
      setAccessResult(result);

      // Also refresh access info
      if (result.hasAccess) {
        try {
          const info = await getUserAccessInfo(user.id);
          setAccessInfo(info);
        } catch (err) {
          devWarn('[AuthContext] Could not refresh access info:', err);
        }
      } else {
        setAccessInfo(null);
      }
    } catch (error) {
      logCriticalError('[AuthContext] Access check refresh failed', error);
      setAccessResult({ hasAccess: false, error: true, errorMessage: 'Access check failed' });
      setAccessInfo(null);
    } finally {
      setCheckingAccess(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          // Disable email confirmation requirement
          data: {
            email_confirm: true
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      logCriticalError('Signup error', err);
      return { data: null, error: err instanceof Error ? err : new Error('Failed to sign up') };
    }
  };

  const signOut = async (navigate: (path: string, opts?: { replace: boolean }) => void) => {
    try {
      // First attempt to clear Supabase session
      try {
        await supabase.auth.setSession(null);
      } catch (err) {
        devWarn('Session clear warning:', err);
        // Continue with cleanup
      }

      // Then sign out from Supabase
      try {
        await supabase.auth.signOut();
      } catch (err) {
        devWarn('Sign out warning:', err);
        // Continue with cleanup
      }

      // Clear all auth-related storage
      try {
        // Clear specific auth items instead of all storage
        localStorage.removeItem('app_version');
        localStorage.removeItem('sb-' + supabase.projectId + '-auth-token');
        sessionStorage.removeItem('sb-' + supabase.projectId + '-auth-token');
      } catch (err) {
        devWarn('Storage clear warning:', err);
      }

      // Clear any cookies
      try {
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.split('=');
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
      } catch (err) {
        devWarn('Cookie clear warning:', err);
      }

      // Clear user state and cache
      setUser(null);
      setNeedsUpdate(false);
      setAccessResult(null);
      setAccessInfo(null);
      setProfile(null);
      setCheckingAccess(false);
      clearAccessCheckCache();

      // Use navigate for cleaner navigation
      navigate('/', { replace: true });

    } catch (err) {
      logCriticalError('Failed to sign out', err);
      // Force cleanup even on error
      setUser(null);
      setAccessResult(null);
      setAccessInfo(null);
      setProfile(null);
      setCheckingAccess(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      needsUpdate,
      accessResult,
      accessInfo,
      profile,
      checkingAccess,
      refreshAccessCheck,
      signIn,
      signUp,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}